<template>
  <manual-page
    title="Primeros Pasos"
    description="Guía completa para comenzar a usar el panel de administración Trunimal."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'quoteRequestsIndex', query: { status: 'pending' } }"
    action-text="Ver solicitudes pendientes"
  >
    <!-- Welcome Section -->
    <h2>Bienvenido al Panel de Administración</h2>
    <p>
      <strong>Trunimal Admin</strong> es el centro de control para gestionar toda la operación
      de la plataforma de alimentación natural para mascotas. Desde aquí podrás administrar
      clientes, mascotas, planes de alimentación, órdenes, franquicias y más.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Desde el panel de administración podrás:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Gestionar clientes y sus mascotas</li>
        <li>Procesar solicitudes de cotización</li>
        <li>Crear y administrar planes de alimentación</li>
        <li>Monitorear órdenes y entregas</li>
        <li>Administrar la red de franquicias</li>
        <li>Configurar el catálogo de alimentos</li>
        <li>Gestionar la tienda de productos</li>
        <li>Revisar contabilidad y reportes</li>
      </ul>
    </div>

    <!-- Navigation Overview -->
    <h2>Navegación del Sistema</h2>
    <p>El menú lateral izquierdo te permite acceder a todas las secciones del sistema:</p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Clientes</h3>
          <p class="text-gray-600">
            Registro y gestión de clientes. Puedes ver sus datos, historial de órdenes,
            mascotas registradas y planes activos.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Mascotas</h3>
          <p class="text-gray-600">
            Lista completa de mascotas registradas. Accede a su información nutricional,
            dietas asignadas, y registros de chequeos.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Solicitudes</h3>
          <p class="text-gray-600">
            Nuevas solicitudes de cotización de clientes potenciales. Aquí revisas la
            información y creas cotizaciones personalizadas.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">4</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Planes</h3>
          <p class="text-gray-600">
            Gestión de planes de alimentación activos, pausados o cancelados.
            Incluye cotizaciones pendientes de aprobación.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">5</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Órdenes</h3>
          <p class="text-gray-600">
            Monitoreo de todas las órdenes del sistema. Filtra por estado: pendientes,
            pagadas, en preparación, listas para envío o entregadas.
          </p>
        </div>
      </div>
    </div>

    <!-- Main Workflow -->
    <h2>Flujo Principal de Operación</h2>
    <p>El ciclo típico de un nuevo cliente en Trunimal sigue estos pasos:</p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Solicitud de Cotización</h3>
          <p class="text-gray-600 mb-2">
            El cliente completa el cuestionario en la app, registrando su mascota y
            respondiendo preguntas sobre su salud y preferencias alimenticias.
          </p>
          <div class="tip-box mt-3">
            <p class="text-sm text-blue-700">
              <strong>Tip:</strong> Revisa las solicitudes pendientes diariamente
              para responder rápidamente a nuevos clientes.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Creación de Cotización</h3>
          <p class="text-gray-600">
            Desde la solicitud, creas una cotización seleccionando el tipo de alimento,
            calculando las porciones según el peso de la mascota, y definiendo el precio.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Aprobación del Cliente</h3>
          <p class="text-gray-600">
            El cliente recibe la cotización, agrega su método de pago y dirección,
            selecciona la frecuencia de entrega, y activa el plan.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">4</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Generación de Órdenes</h3>
          <p class="text-gray-600">
            El sistema genera órdenes automáticamente según la frecuencia seleccionada.
            Cada orden pasa por estados: pendiente de pago → pagada → en preparación → entregada.
          </p>
        </div>
      </div>
    </div>

    <!-- Notifications -->
    <h2>Sistema de Notificaciones</h2>
    <p>
      El icono de campana en la barra superior muestra las notificaciones pendientes.
      Recibirás alertas sobre:
    </p>
    <ul>
      <li><strong>Nuevas solicitudes:</strong> Clientes que solicitan cotización</li>
      <li><strong>Planes aprobados:</strong> Clientes que aceptan cotizaciones</li>
      <li><strong>Problemas de pago:</strong> Cobros fallidos que requieren atención</li>
      <li><strong>Solicitudes de adelanto:</strong> Clientes que piden cambiar fechas de entrega</li>
      <li><strong>Cambios en planes:</strong> Pausas, cancelaciones o reactivaciones</li>
    </ul>

    <!-- Permissions -->
    <h2>Sistema de Permisos</h2>
    <p>
      El acceso a las diferentes secciones del sistema está controlado por roles y permisos.
      Cada usuario tiene asignado un rol que define:
    </p>
    <ul>
      <li>Qué módulos puede ver en el menú</li>
      <li>Qué acciones puede realizar (ver, crear, editar, eliminar)</li>
      <li>Acceso a información sensible (contabilidad, reportes)</li>
    </ul>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Importante:</strong> Si no puedes ver alguna sección o realizar alguna acción,
        contacta al administrador para que revise tus permisos.
      </p>
    </div>

    <!-- Support -->
    <h2>¿Necesitas ayuda?</h2>
    <p>
      Si tienes dudas sobre el uso del sistema, puedes contactar al equipo de soporte:
    </p>
    <ul>
      <li><strong>WhatsApp:</strong> +52 811 669 1329</li>
      <li><strong>Correo:</strong> soporte@trunimal.com</li>
    </ul>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualGettingStarted",

  components: {
    ManualPage,
  },

  data() {
    return {
      relatedLinks: [
        { route: "manualQuoteRequests", label: "Solicitudes de cotización" },
        { route: "manualPlans", label: "Gestión de planes" },
        { route: "manualOrders", label: "Órdenes" },
        { route: "manualUsers", label: "Usuarios y permisos" },
      ],
    };
  },
};
</script>
